import React, { useCallback, useEffect, useState } from 'react';
import Box from '@material-ui/core/Box';
import {
    Button,
    FormControl,
    FormControlProps,
    FormHelperText,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    TextField,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { useSnackbar } from 'notistack';
import { GetApp } from '@material-ui/icons';
import formatISO from 'date-fns/formatISO';
import { Field, FieldProps, Formik } from 'formik';
import * as Yup from 'yup';
import { useSelector } from 'react-redux';
import { UserDto, UserListDto } from 'api/user';
import { DictionaryDto } from 'api/dictionary';
import { CourierDto, fetchActiveCouriers } from 'api/couriers';
import { fetchRegionsDict } from 'api/regions';
import { fetchPaymentsReport, fetchReportByCourier, ReportParam } from 'api/report';
import FormikDatePicker from 'components/atoms/FormikDatePicker/FormikDatePicker';
import { getUserInfo } from '../../store/slices/userInfoSlice';
import { ReportType, reportTypes } from './report-data';
import { ReportForm } from './types/ReportForm';
import { useStyles } from './styles';

const Report: React.FC = () => {
    const classes = useStyles();
    const { enqueueSnackbar } = useSnackbar();
    const [regions, setRegions] = useState<DictionaryDto[]>([]);
    const [selectedFilename, setSelectedFilename] = useState<string>('');
    const [activeCouriers, setActiveCouriers] = useState<(UserListDto | CourierDto)[]>([]);
    const [reportType, setReportType] = useState<ReportType>();
    const { permissions: userPermissions } = useSelector(getUserInfo);
    const reportTypeList = reportTypes.filter((r) =>
        r.permissions.some((permission) =>
            Array.from(userPermissions).includes(permission.toString())
        )
    );

    const [form, setForm] = useState<ReportForm>({
        reportType: ReportType.NotSelected,
        dateFrom: null,
        dateUntil: null,
        regionId: '',
        courier: {},
        // @ts-ignore
        file: {},
    });

    const schema = () =>
        Yup.object().shape({
            reportType: Yup.string().required('Выберите тип отчета'),
            dateFrom: Yup.date()
                .typeError('Неверный формат даты')
                .nullable()
                .required('Выберите дату начала'),
            dateUntil: Yup.date()
                .typeError('Неверный формат даты')
                .nullable()
                .required('Выберите конечную дату'),
        });

    const schemaForNewAddress = () =>
        Yup.object().shape({
            reportType: Yup.string().required('Выберите тип отчета'),
        });

    const handleReportTypeChange = (value: ReportType) => {
        setReportType(value);
    };

    const handleRegionChange = (value: string) => {
        if (value) {
            const code = regions.find((r) => r.id === value)?.code;
            fetchActiveCouriers(code || null).then((res) => {
                setActiveCouriers(res.content);
            });
        } else {
            setActiveCouriers([]);
        }
    };

    const getDictionaries = useCallback(async () => {
        try {
            const regionList = await fetchRegionsDict();
            setRegions(regionList);
            if (regionList.length === 1) {
                fetchActiveCouriers(regionList[0].code || null).then((res) => {
                    setActiveCouriers(res.content);
                });

                setForm({ ...form, regionId: regionList[0].id });
            }
        } catch (e) {
            enqueueSnackbar(`Ошибка при получении данных: ${e.response?.data?.message || e}`, {
                variant: 'error',
            });
        } finally {
        }
        /* eslint-disable react-hooks/exhaustive-deps */
    }, [enqueueSnackbar]);

    useEffect(() => {
        getDictionaries();
    }, [getDictionaries]);

    return (
        <Box>
            <Formik
                enableReinitialize={true}
                initialValues={form}
                validationSchema={
                    reportType === ReportType.NewAddressRegistry ||
                    reportType === ReportType.ReportOnSubdivisions ||
                    reportType === ReportType.ReportOnDeliveryAddress
                        ? schemaForNewAddress()
                        : schema()
                }
                onSubmit={async (values, { setSubmitting }) => {
                    setSubmitting(true);
                    let name = '';
                    let reportData = {};
                    const reportName = reportTypes.find((r) => r.url === values.reportType)?.name;
                    try {
                        if (
                            reportType === ReportType.NewAddressRegistry ||
                            reportType === ReportType.ReportOnSubdivisions ||
                            reportType === ReportType.ReportOnDeliveryAddress
                        ) {
                            name = reportName as string;

                            if (reportType === ReportType.ReportOnDeliveryAddress) {
                                const data = new FormData();
                                data.append('file', values.file);
                                reportData = data;
                            }
                        } else {
                            const dateFrom = formatISO(values.dateFrom as Date, {
                                representation: 'date',
                            });
                            const dateUntil = formatISO(values.dateUntil as Date, {
                                representation: 'date',
                            });
                            const transformPostData = () => {
                                const { reportType } = values;

                                const data: ReportParam = {
                                    date_from: dateFrom,
                                    date_until: dateUntil,
                                    region_id: values.regionId,
                                    courier_id: (values.courier as UserDto)?.id,
                                };

                                if (
                                    reportType === ReportType.ReceiptRequestReport ||
                                    reportType === ReportType.ReportOnRegisteredRpo
                                ) {
                                    data.user_id = (values.courier as UserDto)?.id;
                                    data.courier_id = undefined;
                                }

                                return data;
                            };

                            name = `${reportName} с ${dateFrom} по ${dateUntil}`;
                            reportData = transformPostData();
                        }

                        enqueueSnackbar('Идет формирование отчета, ожидайте.', {
                            variant: 'info',
                        });

                        const config =
                            values.reportType === ReportType.ReportOnDeliveryAddress
                                ? {
                                      headers: { 'Content-Type': 'multipart/form-data' },
                                      data: reportData as FormData,
                                  }
                                : { data: { ...(reportData as ReportParam) } };
                        const response = reportName?.includes('платеж')
                            ? await fetchPaymentsReport(reportData as ReportParam)
                            : await fetchReportByCourier(values.reportType, config);
                        const contentType = response.headers['content-type'];
                        const blob = new Blob([response.data], {
                            type: contentType,
                            endings: 'native',
                        });
                        const link = document.createElement('a');
                        link.href = window.URL.createObjectURL(blob);

                        link.download = `${name}.xlsx`;
                        link.click();
                    } catch (e) {
                        enqueueSnackbar(
                            `Ошибка при выгрузке отчета: ${e.response?.data?.message || e}`,
                            {
                                variant: 'error',
                            }
                        );
                    } finally {
                        setSubmitting(false);
                    }
                }}
            >
                {({ values, isSubmitting, submitForm }) => (
                    <Box position={'relative'}>
                        <Box>
                            <Field name="reportType">
                                {({
                                    field,
                                    form,
                                    meta,
                                    onChange,
                                    ...props
                                }: FieldProps &
                                    React.PropsWithChildren<any> &
                                    FormControlProps) => {
                                    const showError = meta.touched && meta.error;
                                    return (
                                        <FormControl className={classes.formControl}>
                                            <InputLabel id="reportType" error={showError}>
                                                Тип отчета
                                            </InputLabel>
                                            <Select
                                                labelId="reportType"
                                                error={showError}
                                                variant={props.variant}
                                                value={field.value}
                                                onChange={(e: any) => {
                                                    form.setFieldValue(
                                                        'reportType',
                                                        e.target.value
                                                    );

                                                    handleReportTypeChange(e.target.value);
                                                }}
                                            >
                                                {reportTypeList.map((region, index) => (
                                                    <MenuItem key={index} value={region.url}>
                                                        {region.name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                            {showError && (
                                                <FormHelperText error={!!showError}>
                                                    {meta.error}
                                                </FormHelperText>
                                            )}
                                        </FormControl>
                                    );
                                }}
                            </Field>
                        </Box>
                        {values.reportType !== ReportType.NewAddressRegistry &&
                            values.reportType !== ReportType.ReportOnSubdivisions && (
                                <Grid container spacing={3} className={classes.filterRow}>
                                    <Grid item xs={6} md={4} xl={4} lg={4}>
                                        <Field name="dateFrom">
                                            {({ ...props }: FieldProps) => (
                                                <FormikDatePicker
                                                    label="Дата с:"
                                                    inputVariant="standard"
                                                    clearable={true}
                                                    disableToolbar={false}
                                                    {...props}
                                                />
                                            )}
                                        </Field>
                                    </Grid>
                                    <Grid item xs={6} md={4} xl={4} lg={4}>
                                        <Field name="dateUntil">
                                            {({ ...props }: FieldProps) => (
                                                <FormikDatePicker
                                                    label="Дата по:"
                                                    inputVariant="standard"
                                                    clearable={true}
                                                    disableToolbar={false}
                                                    {...props}
                                                />
                                            )}
                                        </Field>
                                    </Grid>
                                    <Grid item xs={6} md={4} xl={4} lg={4}>
                                        <Field name="regionId">
                                            {({
                                                field,
                                                form,
                                                meta,
                                                onChange,
                                                ...props
                                            }: FieldProps &
                                                React.PropsWithChildren<any> &
                                                FormControlProps) => {
                                                const showError = meta.touched && meta.error;
                                                return (
                                                    <FormControl
                                                        className={classes.formControl}
                                                        disabled={regions?.length === 1}
                                                    >
                                                        <InputLabel
                                                            id="region.id"
                                                            error={showError}
                                                        >
                                                            Регион
                                                        </InputLabel>
                                                        <Select
                                                            labelId="region.id"
                                                            error={showError}
                                                            variant={props.variant}
                                                            value={field.value}
                                                            onChange={(e: any) => {
                                                                form.setFieldValue(
                                                                    'regionId',
                                                                    e.target.value
                                                                );

                                                                handleRegionChange(e.target.value);
                                                            }}
                                                        >
                                                            {regions.map((region, index) => (
                                                                <MenuItem
                                                                    key={index}
                                                                    value={region.id}
                                                                >
                                                                    {region.description}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                        {showError && (
                                                            <FormHelperText error={!!showError}>
                                                                {meta.error}
                                                            </FormHelperText>
                                                        )}
                                                    </FormControl>
                                                );
                                            }}
                                        </Field>
                                    </Grid>
                                </Grid>
                            )}
                        <Grid container spacing={3} className={classes.filterRow}>
                            {values.reportType !== ReportType.FreelancerCouriers &&
                                values.reportType !== ReportType.NewAddressRegistry &&
                                values.reportType !== ReportType.ReportOnCheque &&
                                values.reportType !== ReportType.ReportOnSubdivisions && (
                                    <Grid item xs={6} md={4} xl={4} lg={4}>
                                        <Field name="courier">
                                            {({
                                                form,
                                                ...props
                                            }: FieldProps &
                                                React.PropsWithChildren<any> &
                                                FormControlProps) => (
                                                <FormControl className={classes.formControl}>
                                                    <Autocomplete
                                                        id="courier"
                                                        disabled={values.regionId === ''}
                                                        options={activeCouriers}
                                                        getOptionLabel={(
                                                            option: UserListDto | CourierDto
                                                        ) =>
                                                            `${option.last_name} ${option.first_name} (${option.phone_number})`
                                                        }
                                                        //@ts-ignore
                                                        onChange={(
                                                            _event: any,
                                                            newValue:
                                                                | UserListDto
                                                                | CourierDto
                                                                | null
                                                        ) => {
                                                            form.setFieldValue('courier', newValue);
                                                        }}
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                label="Курьер"
                                                                variant="outlined"
                                                            />
                                                        )}
                                                        {...props}
                                                    />
                                                </FormControl>
                                            )}
                                        </Field>
                                    </Grid>
                                )}
                            {values.reportType === ReportType.ReportOnDeliveryAddress && (
                                <Grid item xs={6} md={4} xl={4} lg={4}>
                                    <Field name="file">
                                        {({
                                            form,
                                        }: // ...props
                                        FieldProps &
                                            React.PropsWithChildren<any> &
                                            FormControlProps) => (
                                            <>
                                                <label htmlFor="upload-file">
                                                    <input
                                                        style={{ display: 'none' }}
                                                        id="upload-file"
                                                        type="file"
                                                        onChange={(e) => {
                                                            if (e.target?.files?.length) {
                                                                form.setFieldValue(
                                                                    'file',
                                                                    e.target.files[0]
                                                                );
                                                                setSelectedFilename(
                                                                    e.target.files[0].name
                                                                );
                                                            }
                                                        }}
                                                    />
                                                    <Button
                                                        variant="contained"
                                                        component="span"
                                                        size="large"
                                                    >
                                                        Загрузить файл
                                                    </Button>
                                                </label>
                                                {selectedFilename && (
                                                    <span style={{ marginLeft: '5px' }}>
                                                        {selectedFilename}
                                                    </span>
                                                )}
                                            </>
                                        )}
                                    </Field>
                                </Grid>
                            )}

                            <Grid item xs={6} md={4} xl={4} lg={4}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={submitForm}
                                    startIcon={<GetApp />}
                                    disabled={isSubmitting}
                                >
                                    Скачать
                                </Button>
                            </Grid>
                        </Grid>
                    </Box>
                )}
            </Formik>
        </Box>
    );
};

export default Report;
